import {
    organizationGroupErrorMessage,
    OrganizationGroupRemovalUnconfirmedError,
    useOrganizationGroup,
    useRemoveOrganizationGroupMember,
} from "@/api/organization-groups";
import { AdminPageContent } from "@/components/admin";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { useBreadcrumb } from "@/contexts/shared/breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { formatDate } from "@/utils";
import axios from "axios";
import { useEffect, useState } from "react";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { Link, useParams } from "react-router";
import { AddMemberCombobox } from "./components/AddMemberCombobox";
import { DeleteGroupButton } from "./components/DeleteGroupButton";
import { ExcludedSkillsSection } from "./components/ExcludedSkillsSection";
import { GroupNameField } from "./components/GroupNameField";

const Section = ({
    title,
    description,
    testId,
    children,
}: {
    title: string;
    description?: string;
    testId?: string;
    children: React.ReactNode;
}) => (
    <section
        aria-label={title}
        data-testid={testId}
        className="bg-card rounded-lg border p-4"
    >
        <h2 className="text-foreground text-sm font-semibold">{title}</h2>
        {description && (
            <p className="text-muted-foreground mt-0.5 text-xs">
                {description}
            </p>
        )}
        <div className="mt-3">{children}</div>
    </section>
);

const AdminOrganizationGroupDetails = () => {
    const { groupId } = useParams();
    const { setBreadcrumbs } = useBreadcrumb();
    const parsedId = Number(groupId);
    const id = Number.isSafeInteger(parsedId) && parsedId > 0 ? parsedId : null;
    const {
        data: group,
        error: groupError,
        isError: groupIsError,
        isLoading,
    } = useOrganizationGroup(id);
    const removeMember = useRemoveOrganizationGroupMember();
    const removal = useSingleFlight();
    const [removalFailure, setRemovalFailure] = useState<string | null>(null);

    const remove = async (groupId: number, organizationId: number) => {
        setRemovalFailure(null);
        await removal.run(async () => {
            try {
                await removeMember.mutateAsync({ id: groupId, organizationId });
            } catch (error) {
                if (
                    axios.isAxiosError(error) &&
                    error.response?.status === 404
                ) {
                    return;
                }
                // The unconfirmed-removal error is not an
                // OrganizationGroupError, so the shared helper would replace
                // its message with the generic one.
                setRemovalFailure(
                    error instanceof OrganizationGroupRemovalUnconfirmedError
                        ? error.message
                        : organizationGroupErrorMessage(
                              error,
                              "Couldn't remove the organization. Please try again."
                          )
                );
            }
        });
    };

    useDocumentTitle(group?.name ?? "Group");

    useEffect(() => {
        setBreadcrumbs([
            { label: "Admin", path: "home" },
            { label: "Groups", path: "organization-groups" },
            ...(group ? [{ label: group.name }] : []),
        ]);
    }, [group, setBreadcrumbs]);

    if (isLoading) {
        return (
            <AdminPageContent>
                <Skeleton className="h-8 w-64" />
                <div className="mt-6 grid gap-4">
                    <Skeleton className="h-28 w-full" />
                    <Skeleton className="h-56 w-full" />
                </div>
            </AdminPageContent>
        );
    }

    const groupNotFound =
        groupIsError &&
        axios.isAxiosError(groupError) &&
        groupError.response?.status === 404;

    if (!group || groupNotFound) {
        return (
            <AdminPageContent>
                <p className="text-sm font-medium">Couldn't load this group</p>
                <p className="text-muted-foreground mt-0.5 text-xs">
                    It may have been deleted. Go back to Groups and try again.
                </p>
            </AdminPageContent>
        );
    }

    return (
        <AdminPageContent>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <GroupNameField groupId={group.id} name={group.name} />
                <DeleteGroupButton group={group} />
            </div>

            <div className="mt-6 grid gap-4">
                <Section
                    title="Owner"
                    description="Owns the shared knowledge base."
                    testId="group-owner"
                >
                    <p className="text-sm font-medium">
                        {group.owner_organization.name}
                    </p>
                    <p className="text-muted-foreground mt-0.5 text-xs">
                        Group created {formatDate(group.created_at)}
                    </p>
                </Section>

                <Section
                    title="Members"
                    description="Members read the owner's shared folder."
                    testId="group-members"
                >
                    <AddMemberCombobox groupId={group.id} />

                    <div className="mt-4 overflow-hidden rounded-md border">
                        <Table>
                            <TableHeader className="bg-muted/50">
                                <TableRow>
                                    <TableHead className="text-muted-foreground font-medium">
                                        Organization
                                    </TableHead>
                                    <TableHead className="text-muted-foreground w-24 text-right font-medium">
                                        <span className="sr-only">Actions</span>
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {group.members.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={2}
                                            className="text-muted-foreground h-24 text-center text-sm"
                                        >
                                            No members yet. Add an organization
                                            to share with.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    group.members.map((member) => (
                                        <TableRow key={member.id}>
                                            <TableCell className="min-w-0">
                                                <span className="truncate">
                                                    {member.name}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    aria-label={`Remove ${member.name}`}
                                                    aria-disabled={
                                                        removeMember.isPending
                                                    }
                                                    disabled={
                                                        removeMember.isPending
                                                    }
                                                    onClick={() =>
                                                        void remove(
                                                            group.id,
                                                            member.id
                                                        )
                                                    }
                                                >
                                                    Remove
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                    {removalFailure && (
                        <p
                            role="alert"
                            className="text-destructive mt-2 text-sm"
                        >
                            {removalFailure}
                        </p>
                    )}
                </Section>

                <Section
                    title="Assigned skills"
                    description="Skills scoped to this group."
                    testId="group-skills"
                >
                    {group.assigned_skills.length === 0 ? (
                        <p className="text-muted-foreground text-sm">
                            No skills are scoped to this group.
                        </p>
                    ) : (
                        <ul className="flex flex-wrap gap-2">
                            {group.assigned_skills.map((skill) => (
                                <li
                                    key={skill.id}
                                    className="bg-muted/40 text-muted-foreground rounded-md border px-2 py-1 text-xs"
                                >
                                    {skill.name}
                                </li>
                            ))}
                        </ul>
                    )}
                </Section>

                <ExcludedSkillsSection key={group.id} group={group} />

                <Section
                    title="Assigned checklists"
                    description="Checklists shared with this group."
                    testId="group-checklists"
                >
                    {group.assigned_checklists.length === 0 ? (
                        <p className="text-muted-foreground text-sm">
                            No checklists are shared with this group.
                        </p>
                    ) : (
                        <ul className="flex flex-col gap-1">
                            {group.assigned_checklists.map((checklist) => (
                                <li key={checklist.id} className="min-w-0">
                                    <Link
                                        to={`/admin/checklists/${checklist.id}/details`}
                                        title={checklist.name}
                                        className="text-primary block truncate text-sm font-medium hover:underline"
                                    >
                                        {checklist.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </Section>
            </div>
        </AdminPageContent>
    );
};

export default AdminOrganizationGroupDetails;
